// Story 11.1 — Tabs horizontales del editor con dirty dot + close button.

import { Lock, X } from 'lucide-react';
import { type EditorTab, isDirty } from '@/lib/editor-tabs-reducer';
import { cn } from '@/lib/utils';

interface Props {
  tabs: EditorTab[];
  activePath: string | null;
  onActivate: (path: string) => void;
  onClose: (path: string) => void;
}

function basename(path: string): string {
  const i = path.lastIndexOf('/');
  return i >= 0 ? path.slice(i + 1) : path;
}

export function EditorTabs({ tabs, activePath, onActivate, onClose }: Props): React.ReactElement {
  if (tabs.length === 0) {
    return (
      <div className="flex h-9 items-center px-3 text-xs text-muted-foreground">
        Selecciona un archivo en el árbol para abrirlo.
      </div>
    );
  }
  return (
    <div className="flex h-9 items-stretch overflow-x-auto border-b border-border bg-card/50">
      {tabs.map((tab) => {
        const active = tab.path === activePath;
        const dirty = isDirty(tab);
        return (
          <div
            key={tab.path}
            className={cn(
              'group flex items-center gap-1.5 border-r border-border px-3 text-xs',
              active ? 'bg-background text-foreground' : 'text-muted-foreground hover:bg-muted/50',
            )}
            data-active={active}
            data-dirty={dirty}
          >
            <button
              type="button"
              onClick={() => onActivate(tab.path)}
              className="flex items-center gap-1.5"
              aria-current={active ? 'page' : undefined}
              title={tab.path}
            >
              {tab.readOnly && <Lock className="h-3 w-3" aria-hidden />}
              <span className="max-w-[160px] truncate">{basename(tab.path)}</span>
              {dirty && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-warning"
                  role="status"
                  title="Cambios sin guardar"
                />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.path);
              }}
              aria-label={`Cerrar ${tab.path}`}
              className="rounded p-0.5 opacity-50 hover:bg-muted hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
